/**
 * Formatting utilities — per UX_GUIDE_UNIFIED.md
 *
 * Rules:
 * - Null/missing always renders as em dash (—)
 * - Percentages always to exactly one decimal place
 * - Never truncate values
 * - All numeric values use JetBrains Mono with tabular-nums (handled in CSS)
 */

const EM_DASH = '\u2014';

/**
 * Parse a date string as local time (not UTC).
 * Dataverse dates like "2026-04-01" are parsed by `new Date()` as UTC midnight,
 * which displays as the previous day in US timezones. This forces local interpretation.
 */
export function parseLocalDate(d: string): Date {
  const dateOnly = d.substring(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
    const [y, m, day] = dateOnly.split('-').map(Number) as [number, number, number];
    return new Date(y, m - 1, day);
  }
  return new Date(d);
}

/** Format whole-dollar currency: $4,218,340 */
export function fmtCurrency(n: number | null | undefined): string {
  if (n == null) return EM_DASH;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

/** Format currency with cents: $1,204.87 */
export function fmtCurrencyCents(n: number | null | undefined): string {
  if (n == null) return EM_DASH;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(n);
}

/** Format PMPM: $487.23 */
export function fmtPmpm(n: number | null | undefined): string {
  if (n == null) return EM_DASH;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Format large number with commas: 142,301 */
export function fmtNumber(n: number | null | undefined): string {
  if (n == null) return EM_DASH;
  return new Intl.NumberFormat('en-US').format(n);
}

/** Format percentage to 1 decimal: 12.4% */
export function fmtPercent(n: number | null | undefined): string {
  if (n == null) return EM_DASH;
  return `${n.toFixed(1)}%`;
}

/**
 * Format delta with directional arrow:
 * - Positive: ▲ 3.2%
 * - Negative: ▼ 1.8%
 * - Zero: — 0.0%
 */
export function fmtDelta(n: number | null | undefined): string {
  if (n == null) return EM_DASH;
  if (n > 0) return `▲ ${n.toFixed(1)}%`;
  if (n < 0) return `▼ ${Math.abs(n).toFixed(1)}%`;
  return `— 0.0%`;
}

/** Format display date: Jan 14, 2025 */
export function fmtDate(d: Date | string | null | undefined): string {
  if (d == null) return EM_DASH;
  const date = typeof d === 'string' ? parseLocalDate(d) : d;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Format compact date: 01/14/25 */
export function fmtDateCompact(d: Date | string | null | undefined): string {
  if (d == null) return EM_DASH;
  const date = typeof d === 'string' ? parseLocalDate(d) : d;
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
}

/** Convenience bundle for all formatters */
export const fmt = {
  currency: fmtCurrency,
  currencyCents: fmtCurrencyCents,
  pmpm: fmtPmpm,
  number: fmtNumber,
  percent: fmtPercent,
  delta: fmtDelta,
  date: fmtDate,
  dateCompact: fmtDateCompact,
  parseLocalDate,
  EM_DASH,
} as const;
